/**
 * <InlineChart /> — renders a `query_reports` result inside the chat
 * panel (M22).
 *
 * Tool result shape (`render: "chart"`):
 *   data = { report, chart: { labels, values }, chart_label, ... }
 *
 * A compact Recharts bar chart sized for the 320px panel.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface ChartSeries {
  labels: string[];
  values: number[];
}

function _asChart(data: Record<string, unknown>): {
  series: ChartSeries;
  label: string;
} | null {
  const chart = data.chart as ChartSeries | undefined;
  if (
    !chart ||
    !Array.isArray(chart.labels) ||
    !Array.isArray(chart.values)
  ) {
    return null;
  }
  return {
    series: chart,
    label:
      typeof data.chart_label === "string"
        ? data.chart_label
        : "Report",
  };
}

export function InlineChart({
  summary,
  data,
}: {
  summary: string;
  data: Record<string, unknown>;
}) {
  const chart = _asChart(data);
  if (!chart) {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface-muted px-2.5 py-2 text-xs text-text-secondary">
        {summary}
      </div>
    );
  }

  const points = chart.series.labels.map((label, i) => ({
    label,
    value: chart.series.values[i] ?? 0,
  }));

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-default p-2.5">
      <p className="mb-1 text-xs text-text-secondary">
        {summary}
      </p>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {chart.label}
      </p>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" fontSize={9} />
            <YAxis fontSize={9} allowDecimals={false} width={24} />
            <Tooltip />
            <Bar
              dataKey="value"
              fill="#2260c9"
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
